import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import { SliderCaptcha } from '../components/ui/SliderCaptcha';
import { HoneypotField, isHoneypotTriggered } from '../components/ui/HoneypotField';
import { Package, LogIn, ArrowLeft, Eye, EyeOff, Mail, Shield, Clock } from 'lucide-react';
import {
  performSecurityChecks,
  recordLoginAttempt,
  initializeSecurity,
  getClientIP,
  type LoginSecurityContext,
} from '../lib/security';

interface LoginFormData {
  email: string;
  password: string;
}

export const LoginPage: React.FC = () => {
  const { login, isLoading, error, setError } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  const [honeypotValue, setHoneypotValue] = useState('');
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  const [delaySeconds, setDelaySeconds] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [attemptCount, setAttemptCount] = useState(0);
  const [clientIP, setClientIP] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<LoginFormData>({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const emailValue = watch('email');

  useEffect(() => {
    initializeSecurity();
    getClientIP().then(setClientIP);
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && delaySeconds > 0) {
      setDelaySeconds(0);
    }
  }, [countdown, delaySeconds]);

  const handleCaptchaVerify = useCallback((verified: boolean, token: string) => {
    setCaptchaVerified(verified);
    setCaptchaToken(token);
  }, []);

  const onSubmit = async (data: LoginFormData) => {
    setLoginLoading(true);
    setError(null);

    try {
      const securityContext: LoginSecurityContext = {
        ipAddress: clientIP || 'unknown',
        userAgent: navigator.userAgent,
        email: data.email,
        honeypotTriggered: isHoneypotTriggered(honeypotValue),
        captchaVerified,
      };

      const securityResult = await performSecurityChecks(securityContext);

      if (!securityResult.allowed) {
        if (securityResult.blockedReason) {
          setError(securityResult.blockedReason);
        }

        if (securityResult.requiresCaptcha && !captchaVerified) {
          setShowCaptcha(true);
          setError('Please complete the verification to continue.');
          setLoginLoading(false);
          return;
        }

        if (securityResult.requiresDelay && securityResult.delaySeconds > 0) {
          setDelaySeconds(securityResult.delaySeconds);
          setCountdown(securityResult.delaySeconds);
          setLoginLoading(false);
          return;
        }

        setLoginLoading(false);
        return;
      }

      const result = await login(data.email, data.password);

      if (result.success) {
        await recordLoginAttempt(securityContext, true);
        if (result.redirectTo) {
          navigate(result.redirectTo, { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } else {
        await recordLoginAttempt(securityContext, false);
        setAttemptCount((prev) => prev + 1);

        if (attemptCount >= 2) {
          setShowCaptcha(true);
          setCaptchaVerified(false);
          setCaptchaToken('');
        }

        setError(result.error || 'Login failed');
      }
    } catch (err) {
      console.error('Login exception:', err);
      setError('Login failed. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const isFormDisabled = loginLoading || countdown > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <div className="flex flex-col justify-center min-h-screen px-4 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <Link
            to="/"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>

          <div className="flex items-center justify-center mb-8">
            <div className="bg-primary-600 p-4 rounded-2xl shadow-lg">
              <Package className="h-10 w-10 text-white" />
            </div>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 text-center mb-2">
            Welcome Back
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-center mb-8">
            Sign in to your inventory management account
          </p>
        </div>

        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white dark:bg-gray-800 py-8 px-6 shadow-xl rounded-xl border border-gray-200 dark:border-gray-700">
            {countdown > 0 && (
              <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  <div>
                    <p className="text-amber-700 dark:text-amber-300 font-medium">
                      Please wait before trying again
                    </p>
                    <p className="text-amber-600 dark:text-amber-400 text-sm">
                      {countdown} second{countdown !== 1 ? 's' : ''} remaining
                    </p>
                  </div>
                </div>
              </div>
            )}

            {error && !countdown && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <HoneypotField value={honeypotValue} onChange={setHoneypotValue} />

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email Address
                </label>
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Please enter a valid email address',
                    },
                  })}
                  type="email"
                  className="input"
                  placeholder="Enter your email"
                  disabled={isFormDisabled}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 6,
                        message: 'Password must be at least 6 characters',
                      },
                    })}
                    type={showPassword ? 'text' : 'password'}
                    className="input pr-10"
                    placeholder="Enter your password"
                    disabled={isFormDisabled}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    disabled={isFormDisabled}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.password.message}
                  </p>
                )}
              </div>

              {showCaptcha && !captchaVerified && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <Shield className="h-4 w-4" />
                    <span>Please verify you are human</span>
                  </div>
                  <SliderCaptcha
                    onVerify={handleCaptchaVerify}
                    onError={(err) => setError(err)}
                  />
                </div>
              )}

              {captchaVerified && (
                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                  <Shield className="h-4 w-4" />
                  <span>Verification complete</span>
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                loading={loginLoading}
                disabled={isFormDisabled || (showCaptcha && !captchaVerified)}
              >
                <LogIn className="h-5 w-5 mr-2" />
                Sign In
              </Button>
            </form>

            <div className="mt-8 text-center">
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Don't have an account?{' '}
                <a
                  href="mailto:support@getinv.app"
                  className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300 inline-flex items-center"
                >
                  <Mail className="h-3.5 w-3.5 mr-1" />
                  Contact us
                </a>
              </p>
            </div>
          </div>

          <p className="mt-4 text-center text-xs text-gray-500 dark:text-gray-500">
            Protected by advanced security measures
          </p>
        </div>
      </div>
    </div>
  );
};
